import {
  ALL_MODERATION_PERMISSIONS,
  AppealId,
  ModeratedSurface,
  ModerationCategory,
  ModerationJudge,
  ModerationSubjectType,
  ModerationSurface,
  ReportCaseId,
  ReportReason,
} from "@pax/api-contract/moderation/v1alpha1";
import { createFileRoute } from "@tanstack/react-router";
import { z } from "zod";
import {
  type ModerationLocation,
  ModerationWorkspace,
} from "@/features/moderation/ModerationWorkspace";
import { STATS_PERIODS } from "@/features/moderation/StatsParts";

const ModerationView = z.enum(["queue", "history", "judgements", "users", "stats"]);
const ReportList = z.enum(["reports", "appeals", "lead"]);
const Agreement = z.enum(["agreed", "disagreed"]);
const StatsPeriod = z.coerce
  .number()
  .refine((days): days is (typeof STATS_PERIODS)[number] =>
    (STATS_PERIODS as readonly number[]).includes(days),
  );

export const Route = createFileRoute("/_authenticated/moderation")({
  staticData: { access: { anyOf: [...ALL_MODERATION_PERMISSIONS, "accounts.ban"] } },
  validateSearch: (search: Record<string, unknown>): ModerationLocation => ({
    surface: ModerationSurface.safeParse(search.surface).data,
    view: ModerationView.safeParse(search.view).data,
    list: ReportList.safeParse(search.list).data,
    type: ModerationSubjectType.safeParse(search.type).data,
    reason: ReportReason.safeParse(search.reason).data,
    judge: ModerationJudge.safeParse(search.judge).data,
    category: ModerationCategory.safeParse(search.category).data,
    agreement: Agreement.safeParse(search.agreement).data,
    days: StatsPeriod.safeParse(search.days).data,
    q: z.string().max(64).safeParse(search.q).data,
    caseId: ReportCaseId.safeParse(search.caseId).data,
    appealId: AppealId.safeParse(search.appealId).data,
    userId: z.uuid().safeParse(search.userId).data,
    userSurface: ModeratedSurface.safeParse(search.userSurface).data,
  }),
  head: () => ({ meta: [{ title: "Moderation · Pax.co" }] }),
  component: Page,
});

function Page() {
  const location = Route.useSearch();
  const navigate = Route.useNavigate();
  return (
    <ModerationWorkspace
      location={location}
      navigate={(search, options) => void navigate({ search, replace: options?.replace })}
    />
  );
}
